import React from "react";
import { ArrowRight, BookOpen } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useEffect, useState } from "react";
import { convertWPResponseToPosts } from "@/integrations/wordpress/WordPressPostsProvider";

// Fetch published English articles through WordPress's public REST API.
function usePosts() {
  const [posts, setPosts] = useState<ReturnType<typeof convertWPResponseToPosts>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    const timeout = setTimeout(() => controller.abort(), 20000);
    setLoading(true);
    setError(null);
    fetch("/wp-json/wp/v2/posts?categories=1&categories_exclude=21&per_page=100&status=publish&orderby=date&order=desc&_embed=1", {
      signal: controller.signal
    }).then(response => {
      if (!response.ok) throw new Error("Unable to load articles");
      return response.json();
    }).then(data => {
      if (active) setPosts(convertWPResponseToPosts(data, "post"));
    }).catch(() => {
      if (active) setError("Unable to load articles");
    }).finally(() => {
      clearTimeout(timeout);
      if (active) setLoading(false);
    });
    return () => {
      active = false;
      clearTimeout(timeout);
      controller.abort();
    };
  }, [revision]);
  return {
    posts,
    loading,
    error,
    refetch: () => setRevision(value => value + 1)
  };
}
import { WP_Query } from "@/integrations/wordpress/wp_query";
function stripHtml(html?: string): string {
  if (!html) return "";
  return html.replace(/<[^>]*>?/gm, "").trim();
}
function PostsGridConsumer() {
  const {
    posts,
    loading,
    error,
    refetch
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
        {[...Array(6)].map((_, i) => <div key={i} className="flex flex-col gap-4 rounded-xl border border-border bg-card p-4">
            <Skeleton className="h-52 w-full rounded-lg" />
            <Skeleton className="h-6 w-3/4" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-5/6" />
            <Skeleton className="h-9 w-28 mt-2 rounded-md" />
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="rounded-xl border border-border bg-card p-8 text-center max-w-md mx-auto">
        <p className="text-sm text-muted-foreground mb-4">Unable to load travel guides right now.</p>
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          Try again
        </Button>
      </div>;
  }
  if (!posts || posts.length === 0) {
    return <div className="rounded-xl border border-border bg-card p-12 text-center max-w-lg mx-auto">
        <BookOpen className="size-10 text-muted-foreground mx-auto mb-3" />
        <p className="text-foreground font-medium">No travel guides published yet.</p>
        <p className="text-sm text-muted-foreground mt-1">
          Check back soon for transfer advice and Hurghada destination tips.
        </p>
      </div>;
  }
  return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
      {posts.map((post, i) => {
      const plainExcerpt = stripHtml(post.excerpt);
      return <Card key={post.id} data-index={i} className="flex flex-col justify-between overflow-hidden border border-border bg-card shadow-xs transition-shadow hover:shadow-md">
            <div>
              {post.featuredImage && <div className="relative aspect-16/10 w-full overflow-hidden bg-muted">
                  <img src={post.featuredImage} alt={post.title} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-300 hover:scale-105" />
                </div>}
              <CardHeader className="pt-6">
                <CardTitle className="text-xl font-bold leading-snug tracking-tight text-card-foreground line-clamp-2">
                  {post.title}
                </CardTitle>
              </CardHeader>
              {plainExcerpt && <CardContent className="pt-0">
                  <p className="text-sm text-muted-foreground line-clamp-3 leading-relaxed">
                    {plainExcerpt}
                  </p>
                </CardContent>}
            </div>

            <CardFooter className="pt-4 border-t border-border/60">
              <Button asChild variant="default" size="sm" className="w-full sm:w-auto font-medium">
                <Link to={post.link || "#"}>
                  <span>Read More</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </CardFooter>
          </Card>;
    })}
    </div>;
}
export default function TravelGuides() {
  const queryParams = {
    post_type: "post",
    per_page: 100,
    status: "publish",
    orderby: "date",
    order: "desc"
  };
  const wpQuery = new WP_Query(queryParams);
  return <section id="travelguidesblog" data-nav="light" className="bg-background text-foreground py-16 sm:py-20 lg:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <header className="max-w-2xl mb-12 sm:mb-16">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-foreground font-default">
            Travel Guides
          </h1>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground font-default leading-relaxed">
            Discover travel tips and transfer guides for your Hurghada journey.
          </p>
        </header>

                        <div>
          <PostsGridConsumer />
                </div>
      </div>
    </section>;
}